// useVisionSession (055-vision-entry-point) — the resolve-or-create flow behind
// the restored Vision nav item. The facilitated vision route is session-keyed
// (/facilitation/:sessionId) and sessions are not enumerable-by-engagement
// client-side, so the smallest honest reuse mechanism is a client-side MEMO of
// the last vision session per engagement (matching the 054 spec assumption of
// one open vision session per engagement):
//
//   arcpilot.visionSession.<engagementId>   — linked engagements
//   arcpilot.visionSession.unlinked         — sentinel when no engagement is
//                                             resolved (the session POSTs
//                                             without an engagementId)
//
// Activation contract (spec.md fix outline / UX oracle):
//   1. remembered id → validate via GET /api/sessions/{id}; an OPEN session
//      (ok, not sealed) is REUSED (no POST) — navigate to it;
//   2. no memo / stale (GET non-ok) / sealed → POST /api/sessions (engagementId
//      only when an engagement is resolved; mode left to the server default),
//      remember the new id, navigate.
//
// Sealed detection is defensive: the seal row lives in the fact-event log and
// the session blob does not thread it today, so `sealed === true` or a present
// `sealedAt` both count. A session the GET cannot vouch open-and-alive falls
// back to fresh — never a dead navigation.
//
// Degradation discipline (mirrors useEngagement): never throws; a failed
// create simply does not navigate. Constitution III: the fetches live HERE, in
// a hook — never inside the component.

import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEngagement } from './useEngagement';

const MEMO_PREFIX = 'arcpilot.visionSession.';
const UNLINKED_SENTINEL = 'unlinked';

function memoKey(engagementId: string | null): string {
  return `${MEMO_PREFIX}${engagementId ?? UNLINKED_SENTINEL}`;
}

// localStorage helpers (jsdom/SSR-safe; never throw — house style).
function readMemo(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeMemo(key: string, id: string): void {
  try {
    window.localStorage.setItem(key, id);
  } catch {
    // storage unavailable → no-op; the next activation simply creates again.
  }
}

function clearMemo(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // no-op
  }
}

// GET-validate a remembered session: open means the session resolves AND is
// not sealed. Any failure (non-ok, network, malformed body) reads NOT open.
async function isOpenSession(sessionId: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}`);
    if (!res.ok) return false;
    const body = (await res.json()) as { sealed?: unknown; sealedAt?: unknown } | null;
    if (!body) return false;
    if (body.sealed === true) return false;
    if (body.sealedAt !== undefined && body.sealedAt !== null) return false;
    return true;
  } catch {
    return false;
  }
}

// POST a fresh session — linked to the engagement when one is resolved,
// unlinked otherwise. Resolves null on any failure (never throws).
async function createVisionSession(engagementId: string | null): Promise<string | null> {
  try {
    const res = await fetch('/api/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        experience: 'vision',
        ...(engagementId ? { engagementId } : {}),
      }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { id?: unknown } | null;
    return body && typeof body.id === 'string' ? body.id : null;
  } catch {
    return null;
  }
}

export interface UseVisionSessionReturn {
  /** Resolve (reuse-or-create) the vision session and navigate to it. */
  openVision: () => Promise<void>;
}

export function useVisionSession(): UseVisionSessionReturn {
  const { currentEngagementId } = useEngagement();
  const navigate = useNavigate();
  // In-flight guard: a double activation must not POST twice.
  const busyRef = useRef(false);

  const openVision = useCallback(async (): Promise<void> => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const key = memoKey(currentEngagementId);
      const remembered = readMemo(key);
      if (remembered && (await isOpenSession(remembered))) {
        navigate(`/facilitation/${encodeURIComponent(remembered)}?legacyExperience=vision`);
        return;
      }
      if (remembered) clearMemo(key); // stale or sealed — retire the memo
      const created = await createVisionSession(currentEngagementId);
      if (!created) return; // degradation: no dead navigation on failure
      writeMemo(key, created);
      navigate(`/facilitation/${encodeURIComponent(created)}?legacyExperience=vision`);
    } finally {
      busyRef.current = false;
    }
  }, [currentEngagementId, navigate]);

  return { openVision };
}
